import React, { Fragment, Suspense, useRef } from 'react';
import {
  Box,
  List,
} from '@mui/material';

import { MessageFeedbackView } from '@cvt/components/MessageFeedbackView';

import { useInView } from '@shared/hooks/useInView';

import { useInfiniteAssertions } from '../hooks/useInfiniteAssertions';

interface ViewProps {
  filters?: Assertions.GetListParams;
  renderItem: (job: Assertions.Assertion) => React.ReactNode;
  renderSkeleton: () => React.ReactNode;
}

export const InfiniteAssertionsList: React.FC<ViewProps> = ({ 
  filters = {}, 
  renderItem, 
  renderSkeleton, 
}) => {
  const { assertions, status, fetchNextPage, hasNextPage, isFetching } = useInfiniteAssertions(filters);

  const loadNextRef = useRef<HTMLElement>(undefined);
  const isInView = useInView(loadNextRef);

  const _renderSkeleton = React.useCallback(() => {
    if (isFetching) {
      return Array.from({ length: 3 }).map((v, idx) => <Fragment key={idx}>{renderSkeleton()}</Fragment>);
    }
  }, [isFetching, renderSkeleton]);

  React.useEffect(() => {
    if (isInView && !isFetching && hasNextPage) {
      fetchNextPage();
    }
  }, [isInView, isFetching, fetchNextPage, hasNextPage]);

  return (
    <React.Fragment>
      {status === 'error' && (
        <MessageFeedbackView height="100%" />
      )}
      {status === 'success' && assertions.length === 0 ? (
        <MessageFeedbackView 
          message="No assertions"
          height="100%"
        />
      ) : (
        <List disablePadding>
          {assertions.map(assertion => (
            <Suspense key={assertion.id} fallback={<>{renderSkeleton()}</>}>
              {renderItem(assertion)}
            </Suspense>
          ))}
        </List>
      )}

      <Box ref={loadNextRef} />
      {hasNextPage && _renderSkeleton()}
    </React.Fragment>
  );
};
